import React, { useState, useEffect } from 'react';
import type { VideoMeta } from '../utils/videoUtils';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Alert,
  LinearProgress,
  Select,
  InputLabel,
  FormControl,
  Avatar,
  Divider,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  GlobalStyles
} from '@mui/material';
import { uploadVideo, uploadThumbnail, getVideosMissingThumbnails, getAnalyticsData, uploadPdf, deleteAllWatchtimeAndVideoProgress } from '../utils/videoUtils';
import { collection, getDocs, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { ref as storageRef, deleteObject, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '../config/firebase';
import UserManagement from '../components/admin/UserManagement';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ImageIcon from '@mui/icons-material/Image';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import BarChartIcon from '@mui/icons-material/BarChart';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';

const difficulties = [
  { value: 'beginner', label: 'admin.beginner' },
  { value: 'intermediate', label: 'admin.intermediate' },
  { value: 'advanced', label: 'admin.advanced' },
];

const AdminDashboard: React.FC = () => {
  // Add CSS animations for futuristic effects
  const globalStyles = (
    <GlobalStyles
      styles={{
        '@keyframes shimmer': {
          '0%': {
            transform: 'translateX(-100%)',
          },
          '100%': {
            transform: 'translateX(100%)',
          },
        },
        '@keyframes borderGlow': {
          '0%, 100%': {
            opacity: 0.1,
          },
          '50%': {
            opacity: 0.3,
          },
        },
        '@keyframes rotate': {
          '0%': {
            transform: 'rotate(0deg)',
          },
          '100%': {
            transform: 'rotate(360deg)',
          },
        },
      }}
    />
  );
  const { t } = useTranslation();

  // Upload Video Dialog state
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<number>(0);

  // Upload Video Form state
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [module, setModule] = useState<number>(1);
  const [difficulty, setDifficulty] = useState<'beginner' | 'intermediate' | 'advanced'>('beginner');
  const [isIntro, setIsIntro] = useState(false);
  const [requiredSubscription, setRequiredSubscription] = useState(false);
  const [moduleOrder, setModuleOrder] = useState<number>(1); // Video number in module

  // Upload Thumbnail Dialog state
  const [thumbOpen, setThumbOpen] = useState(false);
  const [thumbLoading, setThumbLoading] = useState(false);
  const [thumbSuccess, setThumbSuccess] = useState<string | null>(null);
  const [thumbError, setThumbError] = useState<string | null>(null);

  const [thumbFile, setThumbFile] = useState<File | null>(null);
  const [thumbVideoId, setThumbVideoId] = useState('');
  const [videosMissingThumb, setVideosMissingThumb] = useState<any[]>([]);

  // Video List State
  const [videos, setVideos] = useState<any[]>([]);
  const [videoListLoading, setVideoListLoading] = useState(true);

  // Add state for edit/delete dialogs
  const [editVideo, setEditVideo] = useState<any | null>(null);
  const [deleteVideo, setDeleteVideo] = useState<any | null>(null);

  // Add state for edit form
  const [editForm, setEditForm] = useState<any | null>(null);
  const [editThumbFile, setEditThumbFile] = useState<File | null>(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [editSuccess, setEditSuccess] = useState<string | null>(null);

  // Add state for delete
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Add state for analytics data
  const [totalUsers, setTotalUsers] = useState<number>(0);
  const [totalVideos, setTotalVideos] = useState<number>(0);
  const [videosStarted, setVideosStarted] = useState<number>(0);
  const [videosCompleted, setVideosCompleted] = useState<number>(0);
  const [totalWatchtime, setTotalWatchtime] = useState<number>(0);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);

  // Add state for delete all progress operation
  const [deleteProgressLoading, setDeleteProgressLoading] = useState(false);
  const [deleteProgressError, setDeleteProgressError] = useState<string | null>(null);
  const [deleteProgressSuccess, setDeleteProgressSuccess] = useState<string | null>(null);

  // PDF Upload Dialog state
  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfSuccess, setPdfSuccess] = useState<string | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [pdfVideoId, setPdfVideoId] = useState('');
  const [videosMissingPdf, setVideosMissingPdf] = useState<any[]>([]);

  // Add filter state for admin video list
  const [selectedModule, setSelectedModule] = useState<number | 'all'>('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState<string>('all');
  const [selectedVideoNumber, setSelectedVideoNumber] = useState<number | 'all'>('all');

  // Get all modules and difficulties from videos
  const moduleOptions = Array.from(new Set(videos.map(v => v.module))).sort((a, b) => a - b);
  const difficultyOptions = Array.from(new Set(videos.map(v => v.difficulty).filter(Boolean)));

  // Filter videos by selected module, difficulty, and video number
  let filteredVideos = videos;
  if (selectedModule !== 'all') {
    filteredVideos = filteredVideos.filter(v => v.module === selectedModule);
  }
  if (selectedDifficulty !== 'all') {
    filteredVideos = filteredVideos.filter(v => v.difficulty === selectedDifficulty);
  }
  // Only show video number dropdown if a module is selected
  const videoNumberOptions = selectedModule !== 'all'
    ? Array.from(new Set(videos.filter(v => v.module === selectedModule).map(v => v.moduleOrder))).sort((a, b) => (a ?? 0) - (b ?? 0))
    : [];
  if (selectedVideoNumber !== 'all' && selectedModule !== 'all') {
    filteredVideos = filteredVideos.filter(v => v.moduleOrder === selectedVideoNumber);
  }
  // Sort by module, then moduleOrder
  filteredVideos = filteredVideos.sort((a, b) => {
    if (a.module !== b.module) return a.module - b.module;
    return (a.moduleOrder ?? 0) - (b.moduleOrder ?? 0);
  });

  // Group filtered videos by type (same as before)
  const groupedVideos: { [key: string]: any[] } = {};
  filteredVideos.forEach((v) => {
    if (v.isIntro) {
      groupedVideos['Intro'] = groupedVideos['Intro'] || [];
      groupedVideos['Intro'].push(v);
    } else if (v.isFree) {
      groupedVideos['Free'] = groupedVideos['Free'] || [];
      groupedVideos['Free'].push(v);
    } else if (typeof v.module === 'number') {
      const label = `Modulo ${v.module}`;
      groupedVideos[label] = groupedVideos[label] || [];
      groupedVideos[label].push(v);
    } else {
      groupedVideos['Other'] = groupedVideos['Other'] || [];
      groupedVideos['Other'].push(v);
    }
  });
  // Sort each group by moduleOrder (ascending), then by title
  Object.keys(groupedVideos).forEach((group) => {
    groupedVideos[group] = groupedVideos[group].sort((a, b) => {
      if (a.moduleOrder !== undefined && b.moduleOrder !== undefined && a.moduleOrder !== b.moduleOrder) {
        return a.moduleOrder - b.moduleOrder;
      }
      if (a.title && b.title) {
        return a.title.localeCompare(b.title);
      }
      return 0;
    });
  });
  const groupOrder = ['Intro', 'Free', ...Object.keys(groupedVideos).filter(k => k.startsWith('Modulo ')).sort((a, b) => {
    const nA = parseInt(a.replace('Modulo ', ''));
    const nB = parseInt(b.replace('Modulo ', ''));
    return nA - nB;
  }), 'Other'];

  // Function to format watchtime from seconds to readable format
  const formatWatchtime = (seconds: number): string => {
    if (seconds < 60) {
      return `${Math.floor(seconds)}s`;
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = Math.floor(seconds % 60);
      return `${minutes}m ${remainingSeconds}s`;
    } else {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const remainingSeconds = Math.floor(seconds % 60);
      return `${hours}h ${minutes}m ${remainingSeconds}s`;
    }
  };

  // Fetch analytics data
  const fetchAnalyticsData = async () => {
    setAnalyticsLoading(true);
    try {
      console.log('Fetching analytics data...');
      
      // Fetch analytics data (includes existing synced data)
      const analyticsData = await getAnalyticsData();
      
      // Fetch users count
      const userSnap = await getDocs(collection(db, 'users'));
      const userList: any[] = userSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Group users by email, count unique emails
      const emailSet = new Set();
      userList.forEach(u => {
        if (u.email) {
          emailSet.add(u.email);
        }
      });
      
      // Fetch videos count
      const videoSnap = await getDocs(collection(db, 'videos'));
      const videoCount = videoSnap.size;
      
      setTotalUsers(emailSet.size);
      setTotalVideos(videoCount);
      setTotalWatchtime(analyticsData.totalWatchtime);
      setVideosStarted(analyticsData.totalVideosStarted);
      setVideosCompleted(analyticsData.totalVideosCompleted);
      
      console.log('Analytics data loaded successfully');
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  // Set up 2-minute interval to sync cookie data and update analytics
  useEffect(() => {
    const updateAnalyticsWithSync = async () => {
      try {
        console.log('Updating analytics with existing Firebase data...');
        
        // Note: Cookie syncing is now handled by individual users during video playback
        // Admin dashboard reads from Firebase data that users sync themselves
        
        // Fetch updated analytics data
        const analyticsData = await getAnalyticsData();
        
        setTotalWatchtime(analyticsData.totalWatchtime);
        setVideosStarted(analyticsData.totalVideosStarted);
        setVideosCompleted(analyticsData.totalVideosCompleted);
        
        console.log('Analytics updated with existing Firebase data');
      } catch (error) {
        console.error('Error updating analytics:', error);
      }
    };

    // Update immediately on mount
    updateAnalyticsWithSync();
    
    // Set up 2-minute interval (120000ms) - only updates analytics from existing Firebase data
    const interval = setInterval(updateAnalyticsWithSync, 120000);
    
    return () => {
      clearInterval(interval);
    };
  }, []);

  // Fetch videos missing thumbnails
  const fetchVideosMissingThumbnails = async () => {
    try {
      const missingThumb = await getVideosMissingThumbnails();
      setVideosMissingThumb(missingThumb);
    } catch (error) {
      console.error('Error fetching videos missing thumbnails:', error);
    }
  };

  // Fetch videos missing PDFs
  const fetchVideosMissingPdf = async () => {
    try {
      const missingPdf = await getVideosMissingThumbnails(); // Using same function for now
      setVideosMissingPdf(missingPdf);
    } catch (error) {
      console.error('Error fetching videos missing PDFs:', error);
    }
  };

  useEffect(() => {
    const fetchVideos = async () => {
      setVideoListLoading(true);
      const snap = await getDocs(collection(db, 'videos'));
      setVideos(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setVideoListLoading(false);
    };
    fetchVideos();
    fetchAnalyticsData();
    fetchVideosMissingThumbnails();
    fetchVideosMissingPdf();
  }, []);

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setFile(null);
    setTitle('');
    setDescription('');
    setCategory('');
    setModule(1);
    setDifficulty('beginner');
    setIsIntro(false);
    setRequiredSubscription(false);
    setModuleOrder(1);
    setSuccess(null);
    setError(null);
    setProgress(0);
  };

  const handleSubmit = async () => {
    if (!file || !title) return;
    setLoading(true);
    setSuccess(null);
    setError(null);
    try {
      // Capitalize difficulty first letter
      const capitalizedDifficulty = difficulty 
        ? (difficulty.charAt(0).toUpperCase() + difficulty.slice(1).toLowerCase()) as 'beginner' | 'intermediate' | 'advanced'
        : difficulty;
      const videoData: VideoMeta = {
        title,
        description,
        category,
        module,
        difficulty: capitalizedDifficulty,
        isIntro,
        requiredSubscription,
        moduleOrder,
      };
      await uploadVideo(file, videoData, (progress) => setProgress(progress));
      setSuccess(t('admin.uploadSuccess'));
      setTimeout(() => handleClose(), 1200);
      // Refresh video list
      const snap = await getDocs(collection(db, 'videos'));
      setVideos(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (e: any) {
      setError(t('admin.uploadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleThumbOpen = () => setThumbOpen(true);
  const handleThumbClose = () => {
    setThumbOpen(false);
    setThumbFile(null);
    setThumbVideoId('');
    setThumbSuccess(null);
    setThumbError(null);
  };

  const handleThumbSubmit = async () => {
    if (!thumbFile || !thumbVideoId) return;
    setThumbLoading(true);
    setThumbSuccess(null);
    setThumbError(null);
    try {
      await uploadThumbnail(thumbFile, thumbVideoId);
      setThumbSuccess(t('admin.thumbnailUploadSuccess'));
      setTimeout(() => handleThumbClose(), 1200);
      fetchVideosMissingThumbnails();
    } catch (e: any) {
      setThumbError(t('admin.thumbnailUploadError'));
    } finally {
      setThumbLoading(false);
    }
  };

  const handlePdfOpen = () => setPdfOpen(true);
  const handlePdfClose = () => {
    setPdfOpen(false);
    setPdfFile(null);
    setPdfVideoId('');
    setPdfSuccess(null);
    setPdfError(null);
  };

  const handlePdfSubmit = async () => {
    if (!pdfFile || !pdfVideoId) return;
    setPdfLoading(true);
    setPdfSuccess(null);
    setPdfError(null);
    try {
      await uploadPdf(pdfFile, pdfVideoId);
      setPdfSuccess(t('admin.pdfUploadSuccess'));
      setTimeout(() => handlePdfClose(), 1200);
      fetchVideosMissingPdf();
    } catch (e: any) {
      setPdfError(t('admin.pdfUploadError'));
    } finally {
      setPdfLoading(false);
    }
  };

  // Edit video handler
  const handleEditSave = async () => {
    if (!editForm) return;
    setEditLoading(true);
    setEditError(null);
    setEditSuccess(null);
    try {
      // 1. If new thumbnail, upload and get URL
      let newThumbUrl = editForm.thumbnailUrl;
      if (editThumbFile) {
        const thumbRef = storageRef(storage, `thumbnails/${Date.now()}_${editThumbFile.name}`);
        await uploadBytesResumable(thumbRef, editThumbFile);
        newThumbUrl = await getDownloadURL(thumbRef);
      }
      // 2. Update Firestore doc - capitalize difficulty first letter
      const capitalizedDifficulty = editForm.difficulty 
        ? editForm.difficulty.charAt(0).toUpperCase() + editForm.difficulty.slice(1).toLowerCase()
        : editForm.difficulty;
      const videoDocRef = doc(db, 'videos', editForm.id);
      await updateDoc(videoDocRef, {
        title: editForm.title,
        description: editForm.description,
        category: editForm.category,
        module: Number(editForm.module),
        difficulty: capitalizedDifficulty,
        isIntro: !!editForm.isIntro,
        requiredSubscription: !!editForm.requiredSubscription,
        isFree: !editForm.requiredSubscription,
        thumbnailUrl: newThumbUrl,
        moduleOrder: Number(editForm.moduleOrder),
      });
      setEditSuccess(t('admin.editSuccess'));
      setTimeout(() => {
        setEditVideo(null);
        setEditForm(null);
        setEditThumbFile(null);
        setEditSuccess(null);
        // Refresh video list
        (async () => {
          const snap = await getDocs(collection(db, 'videos'));
          setVideos(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        })();
      }, 1200);
    } catch (e: any) {
      setEditError(t('admin.editError'));
    } finally {
      setEditLoading(false);
    }
  };

  // Delete dialog confirm handler
  const handleDeleteConfirm = async () => {
    if (!deleteVideo) return;
    setDeleteLoading(true);
    setDeleteError(null);
    try {
      // 1. Delete video file from Storage (if present)
      if (deleteVideo.videoUrl) {
        try {
          await deleteObject(storageRef(storage, deleteVideo.videoUrl.replace(/^.*\/o\//, '').replace(/\?.*$/, '')));
        } catch {}
      }
      // 2. Delete thumbnail from Storage (if present)
      if (deleteVideo.thumbnailUrl) {
        try {
          await deleteObject(storageRef(storage, deleteVideo.thumbnailUrl.replace(/^.*\/o\//, '').replace(/\?.*$/, '')));
        } catch {}
      }
      // 3. Delete Firestore doc
      await deleteDoc(doc(db, 'videos', deleteVideo.id));
      setDeleteVideo(null);
      // Refresh video list
      const snap = await getDocs(collection(db, 'videos'));
      setVideos(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (e: any) {
      setDeleteError(t('admin.deleteError'));
    } finally {
      setDeleteLoading(false);
    }
  };

  // Delete all progress data handler
  const handleDeleteAllProgress = async () => {
    if (!window.confirm(t('admin.deleteAllProgressConfirm'))) {
      return;
    }
    
    setDeleteProgressLoading(true);
    setDeleteProgressError(null);
    setDeleteProgressSuccess(null);
    
    try {
      const result = await deleteAllWatchtimeAndVideoProgress();
      setDeleteProgressSuccess(t('admin.deleteAllProgressSuccess', { 
        progressCount: result.deletedProgressCount, 
        usersCount: result.updatedUsersCount,
        videosCount: result.updatedVideosCount,
        cookiesCount: result.clearedCookiesCount
      }));
      
      // Refresh analytics data
      fetchAnalyticsData();
      
      setTimeout(() => setDeleteProgressSuccess(null), 5000);
    } catch (error: any) {
      setDeleteProgressError(t('admin.deleteAllProgressError'));
      setTimeout(() => setDeleteProgressError(null), 5000);
    } finally {
      setDeleteProgressLoading(false);
    }
  };

  return (
    <>
      {globalStyles}
      <Box sx={{ 
        minHeight: '100vh', 
        backgroundImage: 'url(/martinbg4.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        color: 'black', 
        p: { xs: 1, sm: 3 }, 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'flex-start', 
        py: 6 
      }}>
      <Box
        sx={{
          width: '100%',
          maxWidth: 1200,
          mx: 'auto',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 3,
          alignItems: 'flex-start',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 300, maxWidth: 400 }}>
          {/* Video Management Card - Hybrid Futuristic Design */}
          <Card 
            elevation={0} 
            sx={{ 
              borderRadius: 4, 
              background: 'rgba(45, 45, 45, 0.85)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(227, 28, 37, 0.2)',
              p: 0, 
              minHeight: 420, 
              display: 'flex', 
              flexDirection: 'column', 
              justifyContent: 'space-between',
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)'
            }}
          >
            <CardHeader
              sx={{ 
                pb: 0, 
                alignItems: 'center',
                background: 'linear-gradient(135deg, rgba(227, 28, 37, 0.08) 0%, transparent 100%)',
                borderBottom: '1px solid rgba(227, 28, 37, 0.15)',
                position: 'relative'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{
                  width: 50,
                  height: 50,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #E31C25 0%, #ff4757 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 15px rgba(227, 28, 37, 0.3)',
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: -2,
                    borderRadius: '50%',
                    background: 'linear-gradient(45deg, #E31C25, transparent, #E31C25)',
                    zIndex: -1,
                    opacity: 0.2,
                    animation: 'rotate 3s linear infinite'
                  }
                }}>
                  <PlayCircleOutlineIcon sx={{ color: 'white', fontSize: 28 }} />
                </Box>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700, 
                    color: 'white',
                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                    letterSpacing: '0.5px'
                  }}
                >
                  {t('admin.videoManagement')}
                </Typography>
              </Box>
            </CardHeader>
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3, p: 3 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: 'white', 
                  mb: 2, 
                  fontWeight: 600,
                  textAlign: 'center',
                  textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                }}
              >
                {t('admin.videoManagement')}
              </Typography>
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                gap: 2, 
                mb: 2 
              }}>
                <Button
                  variant="contained"
                  startIcon={<CloudUploadIcon />}
                  onClick={handleOpen}
                  sx={{ 
                    background: 'linear-gradient(135deg, #E31C25 0%, #ff4757 100%)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    py: 1.5,
                    px: 2,
                    borderRadius: 2.5,
                    textTransform: 'none',
                    boxShadow: '0 4px 15px rgba(227, 28, 37, 0.3)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    position: 'relative',
                    overflow: 'hidden',
                    minHeight: 48,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #ff4757 0%, #E31C25 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 20px rgba(227, 28, 37, 0.4)',
                      '&::before': {
                        opacity: 1,
                        left: '100%'
                      }
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
                      transition: 'left 0.5s ease',
                      opacity: 0
                    }
                  }}
                >
                  {t('admin.uploadVideo')}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ImageIcon />}
                  onClick={handleThumbOpen}
                  sx={{ 
                    background: 'rgba(255, 255, 255, 0.05)',
                    color: '#E31C25',
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    py: 1.5,
                    px: 2,
                    borderRadius: 2.5,
                    textTransform: 'none',
                    border: '1.5px solid #E31C25',
                    position: 'relative',
                    overflow: 'hidden',
                    minHeight: 48,
                    '&:hover': {
                      background: 'rgba(227, 28, 37, 0.1)',
                      color: 'white',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 20px rgba(227, 28, 37, 0.2)',
                      borderColor: '#ff4757',
                      '&::before': {
                        opacity: 1,
                        left: '100%'
                      }
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent, rgba(227, 28, 37, 0.15), transparent)',
                      transition: 'left 0.5s ease',
                      opacity: 0
                    }
                  }}
                >
                  {t('admin.uploadThumbnail')}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<CloudUploadIcon />}
                  onClick={handlePdfOpen}
                  sx={{ 
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    py: 1.5,
                    px: 2,
                    borderRadius: 2.5,
                    textTransform: 'none',
                    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
                    border: '1px solid rgba(227, 28, 37, 0.2)',
                    position: 'relative',
                    overflow: 'hidden',
                    minHeight: 48,
                    '&:hover': {
                      background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.08) 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 20px rgba(227, 28, 37, 0.25)',
                      borderColor: '#E31C25',
                      '&::before': {
                        opacity: 1,
                        left: '100%'
                      }
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent, rgba(227, 28, 37, 0.1), transparent)',
                      transition: 'left 0.5s ease',
                      opacity: 0
                    }
                  }}
                >
                  {t('admin.uploadPdf')}
                </Button>
              </Box>
              {/* Refined Filter Section */}
              <Box sx={{ 
                mb: 3, 
                display: 'flex', 
                gap: 2, 
                flexWrap: 'wrap', 
                alignItems: 'center', 
                justifyContent: { xs: 'center', sm: 'flex-start' },
                p: 2.5,
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(227, 28, 37, 0.03) 100%)',
                borderRadius: 2.5,
                border: '1px solid rgba(227, 28, 37, 0.15)',
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '1px',
                  background: 'linear-gradient(90deg, transparent, rgba(227, 28, 37, 0.8), transparent)',
                  opacity: 0.3
                }
              }}>
                <Box>
                  <Typography variant="body2" sx={{ 
                    fontWeight: 600, 
                    mb: 1, 
                    color: '#E31C25',
                    fontSize: '0.85rem',
                    letterSpacing: '0.3px'
                  }}>
                    {t('dashboard.moduleFilter')}
                  </Typography>
                  <select 
                    value={selectedModule} 
                    onChange={e => setSelectedModule(e.target.value === 'all' ? 'all' : Number(e.target.value))} 
                    style={{ 
                      padding: '8px 12px', 
                      borderRadius: 6,
                      background: 'rgba(255, 255, 255, 0.9)',
                      color: 'black',
                      border: '1px solid rgba(227, 28, 37, 0.2)',
                      outline: 'none',
                      fontSize: '0.85rem',
                      minWidth: '120px',
                      backdropFilter: 'blur(10px)'
                    }}
                  >
                    <option value="all">{t('dashboard.allModules')}</option>
                    {moduleOptions.map(m => <option key={m} value={m}>{t('dashboard.moduleLabel', { number: m })}</option>)}
                  </select>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ 
                    fontWeight: 600, 
                    mb: 1, 
                    color: '#E31C25',
                    fontSize: '0.85rem',
                    letterSpacing: '0.3px'
                  }}>
                    {t('dashboard.difficultyFilter')}
                  </Typography>
                  <select 
                    value={selectedDifficulty} 
                    onChange={e => setSelectedDifficulty(e.target.value)} 
                    style={{ 
                      padding: '8px 12px', 
                      borderRadius: 6,
                      background: 'rgba(255, 255, 255, 0.9)',
                      color: 'black',
                      border: '1px solid rgba(227, 28, 37, 0.2)',
                      outline: 'none',
                      fontSize: '0.85rem',
                      minWidth: '120px',
                      backdropFilter: 'blur(10px)'
                    }}
                  >
                    <option value="all">{t('dashboard.allDifficulties')}</option>
                    {difficultyOptions.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </Box>
                {selectedModule !== 'all' && (
                  <Box>
                    <Typography variant="body2" sx={{ 
                      fontWeight: 600, 
                      mb: 1, 
                      color: '#E31C25',
                      fontSize: '0.85rem',
                      letterSpacing: '0.3px'
                    }}>
                      {t('dashboard.videoNumberFilter')}
                    </Typography>
                    <select 
                      value={selectedVideoNumber} 
                      onChange={e => setSelectedVideoNumber(e.target.value === 'all' ? 'all' : Number(e.target.value))} 
                      style={{ 
                        padding: '8px 12px', 
                        borderRadius: 6,
                        background: 'rgba(255, 255, 255, 0.9)',
                        color: 'black',
                        border: '1px solid rgba(227, 28, 37, 0.2)',
                        outline: 'none',
                        fontSize: '0.85rem',
                        minWidth: '120px',
                        backdropFilter: 'blur(10px)'
                      }}
                    >
                      <option value="all">{t('dashboard.allVideoNumbers')}</option>
                      {videoNumberOptions.map(n => <option key={n} value={n}>{t('dashboard.videoNumberLabel', { number: n })}</option>)}
                    </select>
                  </Box>
                )}
              </Box>
              {/* Video List */}
              <Box sx={{ flex: 1, overflowY: 'auto', maxHeight: 260, mb: 1 }}>
                {videoListLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 80 }}>
                    <CircularProgress size={28} />
                  </Box>
                ) : filteredVideos.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
                    {t('common.noVideosFound')}
                  </Typography>
                ) : (
                  Object.keys(groupedVideos).length === 0 ? null : (
                    groupOrder.filter(g => groupedVideos[g] && groupedVideos[g].length > 0).map((group) => (
                      <Box key={group} sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main', mb: 0.5 }}>{group}</Typography>
                        <Divider sx={{ mb: 1 }} />
                        {groupedVideos[group].map((v) => (
                          <Box key={v.id} sx={{ display: 'flex', alignItems: 'center', mb: 1, pl: 1 }}>
                            <Avatar src={v.thumbnailUrl} sx={{ width: 32, height: 32, mr: 1, bgcolor: '#eee' }}/>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" fontWeight={600}>{v.title}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {v.category ? v.category + ' · ' : ''}{t('dashboard.moduleLabel', { number: v.module })}{v.moduleOrder ? ` · ${t('dashboard.videoNumberLabel', { number: v.moduleOrder })}` : ''}
                              </Typography>
                            </Box>
                            <IconButton size="small" color="primary" onClick={() => setEditVideo(v)} sx={{ ml: 1 }}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" color="error" onClick={() => setDeleteVideo(v)} sx={{ ml: 0.5 }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ))}
                      </Box>
                    ))
                  )
                )}
              </Box>
              <Divider sx={{ my: 1 }} />
              {/* End Video List */}
              <Box sx={{ flex: 1, color: '#888', display: 'none', alignItems: 'center', justifyContent: 'center', minHeight: 120 }}>
                <Typography variant="body2" color="text.secondary">{t('common.videoListWillAppear')}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: 1, minWidth: 300, maxWidth: 400 }}>
          {/* User Management Card - Hybrid Futuristic Design */}
          <Card 
            elevation={0} 
            sx={{ 
              borderRadius: 4, 
              background: 'rgba(45, 45, 45, 0.85)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(227, 28, 37, 0.2)',
              minHeight: 420, 
              display: 'flex', 
              flexDirection: 'column', 
              justifyContent: 'space-between',
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)'
            }}
          >
            <CardHeader
              sx={{ 
                pb: 0, 
                alignItems: 'center',
                background: 'linear-gradient(135deg, rgba(227, 28, 37, 0.08) 0%, transparent 100%)',
                borderBottom: '1px solid rgba(227, 28, 37, 0.15)',
                position: 'relative'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{
                  width: 50,
                  height: 50,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #E31C25 0%, #ff4757 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 15px rgba(227, 28, 37, 0.3)',
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: -2,
                    borderRadius: '50%',
                    background: 'linear-gradient(45deg, #E31C25, transparent, #E31C25)',
                    zIndex: -1,
                    opacity: 0.2,
                    animation: 'rotate 3s linear infinite'
                  }
                }}>
                  <PeopleAltIcon sx={{ color: 'white', fontSize: 28 }} />
                </Box>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700, 
                    color: 'white',
                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                    letterSpacing: '0.5px'
                  }}
                >
                  {t('admin.userManagement')}
                </Typography>
              </Box>
            </CardHeader>
            <CardContent sx={{ pt: 2, flex: 1 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: 'white', 
                  mb: 2, 
                  fontWeight: 600,
                  textAlign: 'center',
                  textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                }}
              >
                {t('admin.userManagement')}
              </Typography>
              <UserManagement />
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: 1, minWidth: 300, maxWidth: 400 }}>
          {/* Analytics Card - Hybrid Futuristic Design */}
          <Card 
            elevation={0} 
            sx={{ 
              borderRadius: 4, 
              background: 'rgba(45, 45, 45, 0.85)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(227, 28, 37, 0.2)',
              minHeight: 420, 
              display: 'flex', 
              flexDirection: 'column', 
              justifyContent: 'space-between',
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)'
            }}
          >
            <CardHeader
              sx={{ 
                pb: 0, 
                alignItems: 'center',
                background: 'linear-gradient(135deg, rgba(227, 28, 37, 0.08) 0%, transparent 100%)',
                borderBottom: '1px solid rgba(227, 28, 37, 0.15)',
                position: 'relative'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{
                  width: 50,
                  height: 50,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #E31C25 0%, #ff4757 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 15px rgba(227, 28, 37, 0.3)',
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: -2,
                    borderRadius: '50%',
                    background: 'linear-gradient(45deg, #E31C25, transparent, #E31C25)',
                    zIndex: -1,
                    opacity: 0.2,
                    animation: 'rotate 3s linear infinite'
                  }
                }}>
                  <BarChartIcon sx={{ color: 'white', fontSize: 28 }} />
                </Box>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700, 
                    color: 'white',
                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                    letterSpacing: '0.5px'
                  }}
                >
                  {t('admin.analytics')}
                </Typography>
              </Box>
            </CardHeader>
            <CardContent sx={{ flex: 1, p: 3 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: 'white', 
                  mb: 3, 
                  fontWeight: 600,
                  textAlign: 'center',
                  textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                }}
              >
                {t('admin.analytics')}
              </Typography>
              {deleteProgressSuccess && <Alert severity="success" sx={{ mb: 2 }}>{deleteProgressSuccess}</Alert>}
              {deleteProgressError && <Alert severity="error" sx={{ mb: 2 }}>{deleteProgressError}</Alert>}
              
              {/* Clean Analytics Grid */}
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                gap: 2,
                mb: 3
              }}>
                {/* Total Users */}
                <Box sx={{
                  p: 3,
                  borderRadius: 2,
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(227, 28, 37, 0.2)',
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.08)',
                    borderColor: 'rgba(227, 28, 37, 0.4)'
                  }
                }}>
                  <Typography variant="body2" sx={{ 
                    color: 'rgba(255, 255, 255, 0.7)', 
                    mb: 1,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '1px'
                  }}>
                    {t('admin.totalUsers')}
                    </Typography>
                  <Typography variant="h3" sx={{ 
                    fontWeight: 700, 
                    color: '#E31C25',
                    fontSize: '2.5rem'
                  }}>
                    {analyticsLoading ? <CircularProgress size={32} sx={{ color: '#E31C25' }} /> : totalUsers}
                    </Typography>
                </Box>

                {/* Total Videos */}
                <Box sx={{
                  p: 3,
                  borderRadius: 2,
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(227, 28, 37, 0.2)',
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.08)',
                    borderColor: 'rgba(227, 28, 37, 0.4)'
                  }
                }}>
                  <Typography variant="body2" sx={{ 
                    color: 'rgba(255, 255, 255, 0.7)', 
                    mb: 1,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '1px'
                  }}>
                    {t('admin.totalVideos')}
                    </Typography>
                  <Typography variant="h3" sx={{ 
                    fontWeight: 700, 
                    color: '#ff4757',
                    fontSize: '2.5rem'
                  }}>
                    {analyticsLoading ? <CircularProgress size={32} sx={{ color: '#ff4757' }} /> : totalVideos}
                    </Typography>
                </Box>

                {/* Videos Completed */}
                <Box sx={{
                  p: 3,
                  borderRadius: 2,
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(227, 28, 37, 0.2)',
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.08)',
                    borderColor: 'rgba(227, 28, 37, 0.4)'
                  }
                }}>
                  <Typography variant="body2" sx={{ 
                    color: 'rgba(255, 255, 255, 0.7)', 
                    mb: 1,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '1px'
                  }}>
                    {t('admin.videosCompleted')}
                    </Typography>
                  <Typography variant="h3" sx={{ 
                    fontWeight: 700, 
                    color: '#4CAF50',
                    fontSize: '2.5rem'
                  }}>
                    {analyticsLoading ? <CircularProgress size={32} sx={{ color: '#4CAF50' }} /> : videosCompleted}
                  </Typography>
                </Box>

                {/* Videos Started */}
                <Box sx={{
                  p: 3,
                  borderRadius: 2,
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(227, 28, 37, 0.2)',
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.08)',
                    borderColor: 'rgba(227, 28, 37, 0.4)'
                  }
                }}>
                  <Typography variant="body2" sx={{ 
                    color: 'rgba(255, 255, 255, 0.7)', 
                    mb: 1,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '1px'
                  }}>
                    {t('admin.videosStarted')}
                  </Typography>
                  <Typography variant="h3" sx={{ 
                    fontWeight: 700, 
                    color: '#2196F3',
                    fontSize: '2.5rem'
                  }}>
                    {analyticsLoading ? <CircularProgress size={32} sx={{ color: '#2196F3' }} /> : videosStarted}
                  </Typography>
                </Box>
              </Box>

              {/* Watchtime - Full Width */}
              <Box sx={{
                p: 3,
                borderRadius: 2,
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(227, 28, 37, 0.2)',
                textAlign: 'center',
                mb: 3,
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.08)',
                  borderColor: 'rgba(227, 28, 37, 0.4)'
                }
              }}>
                <Typography variant="body2" sx={{ 
                  color: 'rgba(255, 255, 255, 0.7)', 
                  mb: 1,
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '1px'
                }}>
                  {t('admin.totalWatchtime')}
                </Typography>
                <Typography variant="h3" sx={{ 
                  fontWeight: 700, 
                  color: '#FF9800',
                  fontSize: '2.5rem'
                }}>
                  {analyticsLoading ? <CircularProgress size={32} sx={{ color: '#FF9800' }} /> : formatWatchtime(totalWatchtime)}
                </Typography>
              </Box>

              {/* Delete Button */}
              <Box sx={{ textAlign: 'center' }}>
                    <Button 
                      variant="contained" 
                      onClick={handleDeleteAllProgress}
                      disabled={deleteProgressLoading}
                      startIcon={deleteProgressLoading ? <CircularProgress size={18} /> : null}
                  sx={{ 
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #E31C25 0%, #ff4757 100%)',
                    color: 'white',
                    fontWeight: 600,
                    py: 1.5,
                    px: 4,
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    fontSize: '0.85rem',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #ff4757 0%, #E31C25 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 20px rgba(227, 28, 37, 0.4)'
                    }
                  }}
                    >
                      {deleteProgressLoading ? t('admin.deletingProgress') : t('admin.deleteAllProgress')}
                    </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>
      {/* Upload Video Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{t('admin.uploadVideo')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {success && <Alert severity="success">{success}</Alert>}
          {error && <Alert severity="error">{error}</Alert>}
          {loading && <LinearProgress variant="determinate" value={progress} sx={{ mb: 2 }} />}
          <Button variant="outlined" component="label" color="primary" sx={{ borderRadius: 2 }}>
            {file ? file.name : t('admin.selectVideoFile')}
            <input type="file" accept="video/*" hidden onChange={e => setFile(e.target.files?.[0] || null)} />
          </Button>
          <TextField label={t('admin.title')} value={title} onChange={e => setTitle(e.target.value)} required fullWidth />
          <TextField label={t('admin.description')} value={description} onChange={e => setDescription(e.target.value)} multiline rows={2} fullWidth />
          <TextField label={t('admin.category')} value={category} onChange={e => setCategory(e.target.value)} fullWidth />
          <TextField label={t('admin.module')} type="number" inputProps={{ min: 1, max: 12 }} value={module} onChange={e => setModule(Number(e.target.value))} fullWidth />
          <TextField label={t('admin.videoNumberInModule')} type="number" inputProps={{ min: 1 }} value={moduleOrder} onChange={e => setModuleOrder(Number(e.target.value))} required fullWidth />
          <TextField select label={t('admin.difficulty')} value={difficulty} onChange={e => setDifficulty(e.target.value as any)} fullWidth>
            {difficulties.map(opt => <MenuItem key={opt.value} value={opt.value}>{t(opt.label)}</MenuItem>)}
          </TextField>
          <FormControlLabel control={<Checkbox checked={isIntro} onChange={e => setIsIntro(e.target.checked)} />} label={t('admin.introductionVideo')} />
          <FormControlLabel control={<Checkbox checked={requiredSubscription} onChange={e => setRequiredSubscription(e.target.checked)} />} label={t('admin.requiresSubscription')} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>{t('common.cancel')}</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary" disabled={!file || !title || loading} startIcon={loading ? <CircularProgress size={18} /> : null} sx={{ borderRadius: 2 }}>
            {t('common.upload')}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Upload Thumbnail Dialog */}
      <Dialog open={thumbOpen} onClose={handleThumbClose} maxWidth="sm" fullWidth>
        <DialogTitle>{t('admin.uploadThumbnail')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {thumbSuccess && <Alert severity="success">{thumbSuccess}</Alert>}
          {thumbError && <Alert severity="error">{thumbError}</Alert>}
          {thumbLoading && <LinearProgress variant="indeterminate" sx={{ mb: 2 }} />}
          <Button variant="outlined" component="label" color="secondary" sx={{ borderRadius: 2 }}>
            {thumbFile ? thumbFile.name : t('admin.selectThumbnailFile')}
            <input type="file" accept="image/*" hidden onChange={e => setThumbFile(e.target.files?.[0] || null)} />
          </Button>
          <FormControl fullWidth>
            <InputLabel id="video-select-label">{t('common.selectVideo')}</InputLabel>
            <Select
              labelId="video-select-label"
              value={thumbVideoId}
              label={t('common.selectVideo')}
              onChange={e => setThumbVideoId(e.target.value)}
              disabled={videosMissingThumb.length === 0}
            >
              {videosMissingThumb.length === 0 && <MenuItem value="" disabled>{t('common.noVideosAvailable')}</MenuItem>}
              {videosMissingThumb.map((v: any) => (
                <MenuItem key={v.id} value={v.id}>
                  {v.title} ({t('dashboard.moduleLabel', { number: v.module })}, {v.category})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleThumbClose} disabled={thumbLoading}>{t('common.cancel')}</Button>
          <Button onClick={handleThumbSubmit} variant="contained" color="secondary" disabled={!thumbFile || !thumbVideoId || thumbLoading} startIcon={thumbLoading ? <CircularProgress size={18} /> : null} sx={{ borderRadius: 2 }}>
            {t('common.upload')}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Edit Video Dialog */}
      <Dialog open={!!editVideo} onClose={() => setEditVideo(null)} maxWidth="sm" fullWidth scroll="paper" sx={{ mt: 6 }}>
        <DialogTitle>{t('admin.editVideo')}</DialogTitle>
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            maxHeight: { xs: '70vh', sm: 500 },
            overflowY: 'auto',
            pt: 2, // Add padding top for space below the title
          }}
        >
          {editError && <Alert severity="error">{editError}</Alert>}
          {editSuccess && <Alert severity="success">{editSuccess}</Alert>}
          <TextField label={t('admin.title')} value={editForm?.title || ''} onChange={e => setEditForm((f: any) => ({ ...f, title: e.target.value }))} fullWidth />
          <TextField label={t('admin.description')} value={editForm?.description || ''} onChange={e => setEditForm((f: any) => ({ ...f, description: e.target.value }))} multiline rows={2} fullWidth />
          <TextField label={t('admin.category')} value={editForm?.category || ''} onChange={e => setEditForm((f: any) => ({ ...f, category: e.target.value }))} fullWidth />
          <TextField label={t('admin.module')} type="number" inputProps={{ min: 1, max: 12 }} value={editForm?.module || 1} onChange={e => setEditForm((f: any) => ({ ...f, module: Number(e.target.value) }))} fullWidth />
          <TextField label={t('admin.videoNumberInModule')} type="number" inputProps={{ min: 1 }} value={editForm?.moduleOrder || 1} onChange={e => setEditForm((f: any) => ({ ...f, moduleOrder: Number(e.target.value) }))} required fullWidth />
          <TextField select label={t('admin.difficulty')} value={editForm?.difficulty || 'beginner'} onChange={e => setEditForm((f: any) => ({ ...f, difficulty: e.target.value }))} fullWidth>
            {difficulties.map(opt => <MenuItem key={opt.value} value={opt.value}>{t(opt.label)}</MenuItem>)}
          </TextField>
          <FormControlLabel control={<Checkbox checked={!!editForm?.isIntro} onChange={e => setEditForm((f: any) => ({ ...f, isIntro: e.target.checked }))} />} label={t('admin.introductionVideo')} />
          <FormControlLabel control={<Checkbox checked={!!editForm?.requiredSubscription} onChange={e => setEditForm((f: any) => ({ ...f, requiredSubscription: e.target.checked }))} />} label={t('admin.requiresSubscription')} />
          <Box>
            <Button variant="outlined" component="label" color="secondary" sx={{ borderRadius: 2 }}>
              {editThumbFile ? editThumbFile.name : t('admin.changeThumbnail')}
              <input type="file" accept="image/*" hidden onChange={e => setEditThumbFile(e.target.files?.[0] || null)} />
            </Button>
            {editForm?.thumbnailUrl && !editThumbFile && (
              <Box sx={{ mt: 1 }}>
                <img src={editForm.thumbnailUrl} alt="Current Thumbnail" style={{ maxWidth: 120, borderRadius: 4 }} />
              </Box>
            )}
            {editThumbFile && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption">{t('admin.newThumbnailSelected')}</Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditVideo(null)} disabled={editLoading}>{t('common.cancel')}</Button>
          <Button onClick={handleEditSave} variant="contained" color="primary" disabled={editLoading} startIcon={editLoading ? <CircularProgress size={18} /> : null} sx={{ borderRadius: 2 }}>
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Video Dialog */}
      <Dialog open={!!deleteVideo} onClose={() => setDeleteVideo(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('admin.deleteVideo')}</DialogTitle>
        <DialogContent>
          {deleteError && <Alert severity="error">{deleteError}</Alert>}
          <Typography>{t('admin.deleteVideoConfirm')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteVideo(null)} disabled={deleteLoading}>{t('common.cancel')}</Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error" disabled={deleteLoading} startIcon={deleteLoading ? <CircularProgress size={18} /> : null} sx={{ borderRadius: 2 }}>
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* PDF Upload Dialog */}
      <Dialog open={pdfOpen} onClose={handlePdfClose} maxWidth="sm" fullWidth>
        <DialogTitle>{t('admin.uploadPdf')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {pdfSuccess && <Alert severity="success">{pdfSuccess}</Alert>}
          {pdfError && <Alert severity="error">{pdfError}</Alert>}
          {pdfLoading && <LinearProgress variant="indeterminate" sx={{ mb: 2 }} />}
          <Button variant="outlined" component="label" color="success" sx={{ borderRadius: 2 }}>
            {pdfFile ? pdfFile.name : t('admin.selectPdfFile')}
            <input type="file" accept="application/pdf" hidden onChange={e => setPdfFile(e.target.files?.[0] || null)} />
          </Button>
          <FormControl fullWidth>
            <InputLabel id="pdf-video-select-label">{t('common.selectVideo')}</InputLabel>
            <Select
              labelId="pdf-video-select-label"
              value={pdfVideoId}
              label={t('common.selectVideo')}
              onChange={e => setPdfVideoId(e.target.value)}
              disabled={videosMissingPdf.length === 0}
            >
              {videosMissingPdf.length === 0 && <MenuItem value="" disabled>{t('common.noVideosAvailable')}</MenuItem>}
              {videosMissingPdf.map((v: any) => (
                <MenuItem key={v.id} value={v.id}>
                  {v.title} ({t('dashboard.moduleLabel', { number: v.module })}, {v.category})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePdfClose} disabled={pdfLoading}>{t('common.cancel')}</Button>
          <Button onClick={handlePdfSubmit} variant="contained" color="success" disabled={!pdfFile || !pdfVideoId || pdfLoading} startIcon={pdfLoading ? <CircularProgress size={18} /> : null} sx={{ borderRadius: 2 }}>
            {t('common.upload')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </>
  );
};

export default AdminDashboard;
